import { z } from 'zod'
import {
  chatMessageSchema,
  chatSessionBrandSelectionSchema,
  chatSessionCreateSchema,
  chatSessionRenameSchema,
  chatSessionSchema,
  chatSessionUpdateSchema,
  type ChatMessage,
  type ChatSession,
  type ChatSessionCreate,
  type ChatSessionBrandSelection,
  type ChatSessionUpdate,
  type SlashCommandKind,
} from '@mindkeep/shared'
import { apiRequest, buildAuthedHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'

const sessionListResponseSchema = z.object({ sessions: z.array(chatSessionSchema) })
const messageListResponseSchema = z.object({ messages: z.array(chatMessageSchema) })
const messageResponseSchema = z.object({ message: chatMessageSchema })

export type { ChatSession, ChatMessage, ChatSessionCreate, ChatSessionBrandSelection, ChatSessionUpdate }

export function listSessions() {
  return apiRequest({ path: '/chat/sessions', schema: sessionListResponseSchema })
}

export function createSession(body: ChatSessionCreate = {}) {
  return apiRequest({
    method: 'POST',
    path: '/chat/sessions',
    body: chatSessionCreateSchema.parse(body),
    schema: chatSessionSchema,
  })
}

export function renameSession(sessionId: string, title: string) {
  return apiRequest({
    method: 'PATCH',
    path: `/chat/sessions/${sessionId}`,
    body: chatSessionRenameSchema.parse({ title }),
    schema: chatSessionSchema,
  })
}

export function updateSession(sessionId: string, body: ChatSessionUpdate) {
  return apiRequest({
    method: 'PATCH',
    path: `/chat/sessions/${sessionId}`,
    body: chatSessionUpdateSchema.parse(body),
    schema: chatSessionSchema,
  })
}

export function updateSessionBrandSelection(
  sessionId: string,
  body: ChatSessionBrandSelection,
) {
  return apiRequest({
    method: 'PATCH',
    path: `/chat/sessions/${sessionId}/brand-selection`,
    body: chatSessionBrandSelectionSchema.parse(body),
    schema: chatSessionSchema,
  })
}

export async function deleteSession(sessionId: string): Promise<void> {
  await apiRequest({
    method: 'DELETE',
    path: `/chat/sessions/${sessionId}`,
    schema: z.unknown(),
  })
}

export function listMessages(sessionId: string) {
  return apiRequest({
    path: `/chat/sessions/${sessionId}/messages`,
    schema: messageListResponseSchema,
  })
}

export function persistSlashUserMessage(
  sessionId: string,
  content: string,
  slashCommand: SlashCommandKind,
) {
  return apiRequest({
    method: 'POST',
    path: `/chat/sessions/${sessionId}/user-message`,
    body: { content, slash_command: slashCommand },
    schema: messageResponseSchema,
  })
}

export function persistSlashAssistantStub(
  sessionId: string,
  payload: {
    content: string
    slash_command: SlashCommandKind
    slash_status: 'completed' | 'failed'
    vault_path?: string
    error?: string
  },
) {
  return apiRequest({
    method: 'POST',
    path: `/chat/sessions/${sessionId}/assistant-stub`,
    body: payload,
    schema: messageResponseSchema,
  })
}

// ---- Streaming ----

export type StreamEvent =
  | { type: 'user_message'; message: ChatMessage }
  | { type: 'session_update'; id: string; title: string }
  | { type: 'token'; content: string }
  | { type: 'done'; message: ChatMessage }
  | { type: 'error'; message: string }

import type { AttachmentRef } from './attachments-api'

interface StreamMessageOptions {
  sessionId: string
  content: string
  attachments?: AttachmentRef[]
  searchVault?: boolean
  onEvent: (event: StreamEvent) => void
  signal?: AbortSignal
}

export async function streamMessage({
  sessionId,
  content,
  attachments,
  searchVault,
  onEvent,
  signal,
}: StreamMessageOptions): Promise<void> {
  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')

  const response = await fetch(`${baseUrl}/api/chat/sessions/${sessionId}/messages`, {
    method: 'POST',
    headers: await buildAuthedHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    }),
    body: JSON.stringify({ content, attachments, search_vault: searchVault }),
    signal,
  })

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = (await response.json()) as { error?: { message?: string } }
      message = body.error?.message ?? message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  if (!response.body) {
    throw new Error('Keine Stream-Antwort')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let separatorIdx = buffer.indexOf('\n\n')
      while (separatorIdx !== -1) {
        const rawEvent = buffer.slice(0, separatorIdx)
        buffer = buffer.slice(separatorIdx + 2)

        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue
          const payload = line.slice(5).trim()
          if (!payload) continue
          try {
            const parsed = JSON.parse(payload) as StreamEvent
            onEvent(parsed)
          } catch (err) {
            console.error('SSE parse error', err, payload)
          }
        }

        separatorIdx = buffer.indexOf('\n\n')
      }
    }
  } finally {
    reader.releaseLock()
  }
}
